import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, CalendarX2, CreditCard, Download, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { PackageTag } from '@/components/package-tag';
import { localizedName } from '@/components/localized-name';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePermission } from '@/features/auth/auth.store';
import { EVENT_TYPE_FILTERS } from '@/features/events/event-enums';
import { RecordPaymentDialog } from '@/features/events/record-payment-dialog';
import { formatDateTime, formatPhone } from '@/lib/format';
import { toApiDate as apiDate } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import { BookingDetailSheet } from './booking-detail-sheet';
import { BookingsTabs } from './bookings-tabs';
import {
  BookingAmount,
  CATEGORY_FILTER_ORDER,
  PaymentCategoryCell,
  categoryLabelKey,
} from './payment-category';
import {
  useBookingHosts,
  useBookingLocations,
  useBookingSearch,
  useBookingServiceTypes,
  useExportBookings,
  type BookingSearchModel,
} from './bookings.api';
import { BOOKING_STATUS, PAYMENT_CATEGORY, type AdminBookingRow } from './types';

/**
 * Booking management — the grid that used to be the "Bookings" tab of
 * `/booking/sales`, now a page of its own.
 *
 * Everything is server-side: `v2/admin/bookings/search` takes the whole filter
 * set and answers `{ total, page, size, bookings }`, with `paid`, `refunded`
 * and `outstanding` already computed per row.
 */

const ANY = 'ANY';

function startOfMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
}

function endOfMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
}

function statusBadge(status: number | null, t: (k: string) => string) {
  if (status === BOOKING_STATUS.confirmed) {
    return <Badge variant="success">{t('manageBookings.confirmed')}</Badge>;
  }
  if (status === BOOKING_STATUS.cancelled) {
    return <Badge variant="destructive">{t('manageBookings.cancelled')}</Badge>;
  }
  return <Badge variant="warning">{t('manageBookings.pending')}</Badge>;
}

export function ManageBookingsPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const canManageMoney = usePermission('isFinance');

  /*
   * The filters seed from the URL so a tile on the Sales report can hand over a
   * view of exactly the bookings behind its number. Read once, as the initial
   * state: after that the controls own it, and re-reading would fight them.
   */
  const [params] = useSearchParams();
  const seed = (key: string, fallback: string) => params.get(key) ?? fallback;

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [start, setStart] = useState(() => seed('start', startOfMonth()));
  const [end, setEnd] = useState(() => seed('end', endOfMonth()));
  const [locationId, setLocationId] = useState(() => seed('locationId', ANY));
  const [eventType, setEventType] = useState(() => seed('eventType', ANY));
  const [serviceTypeId, setServiceTypeId] = useState(() =>
    seed('serviceTypeId', ANY)
  );
  const [hostId, setHostId] = useState(() => seed('hostId', ANY));
  const [paymentCategory, setPaymentCategory] = useState(() =>
    seed('paymentCategory', ANY)
  );
  const [status, setStatus] = useState(() => seed('status', ANY));
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [paying, setPaying] = useState<AdminBookingRow | null>(null);

  const { data: locations } = useBookingLocations();
  const { data: serviceTypes } = useBookingServiceTypes();
  const { data: hosts } = useBookingHosts();

  const model: BookingSearchModel = useMemo(
    () => ({
      startDate: apiDate(start, false),
      endDate: apiDate(end, true),
      locationId: locationId === ANY ? null : locationId,
      eventId: null,
      serviceTypeId: serviceTypeId === ANY ? null : serviceTypeId,
      hostId: hostId === ANY ? null : hostId,
      eventType: eventType === ANY ? null : Number(eventType),
      paymentCategory:
        paymentCategory === ANY ? null : Number(paymentCategory),
      status: status === ANY ? null : Number(status),
      checkedIn: null,
      query,
      page,
      size,
    }),
    [start, end, locationId, serviceTypeId, hostId, eventType, paymentCategory, status, query, page, size]
  );

  const search = useBookingSearch(model, Boolean(start && end));
  const exportRows = useExportBookings();

  const rows = search.data?.bookings ?? [];
  const total = search.data?.total ?? 0;

  /** Any change to what is being asked for resets to the first page. */
  const reset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const filterFields: FilterField[] = [
    {
      id: 'from',
      type: 'date',
      label: t('manageBookings.from'),
      value: start,
      onChange: reset(setStart),
      // A date has no "any"; it never counts as narrowing.
      resetValue: start,
    },
    {
      id: 'to',
      type: 'date',
      label: t('manageBookings.to'),
      value: end,
      onChange: reset(setEnd),
      resetValue: end,
    },
    {
      id: 'location',
      label: t('manageBookings.location'),
      value: locationId,
      onChange: reset(setLocationId),
      options: [
        { value: ANY, label: t('manageBookings.any') },
        ...(locations ?? []).map((l) => ({
          value: l.id,
          label: l.name ?? '',
        })),
      ],
    },
    {
      id: 'eventType',
      label: t('manageBookings.eventType'),
      value: eventType,
      onChange: reset(setEventType),
      options: [
        { value: ANY, label: t('manageBookings.any') },
        ...EVENT_TYPE_FILTERS.map((o) => ({
          value: String(o.value),
          label: t(o.labelKey),
        })),
      ],
    },
    {
      id: 'serviceType',
      label: t('manageBookings.serviceType'),
      value: serviceTypeId,
      onChange: reset(setServiceTypeId),
      options: [
        { value: ANY, label: t('manageBookings.any') },
        ...(serviceTypes ?? []).map((s) => ({
          value: s.id,
          label: localizedName(s, language),
        })),
      ],
    },
    {
      id: 'host',
      label: t('manageBookings.host'),
      value: hostId,
      onChange: reset(setHostId),
      options: [
        { value: ANY, label: t('manageBookings.any') },
        ...(hosts ?? []).map((h) => ({
          value: h.id,
          label: [h.firstName, h.lastName].filter(Boolean).join(' '),
        })),
      ],
    },
    {
      id: 'paidHow',
      label: t('manageBookings.paidHow'),
      value: paymentCategory,
      onChange: reset(setPaymentCategory),
      // Money first, problems last — the order carries meaning.
      sort: false,
      options: [
        { value: ANY, label: t('manageBookings.any') },
        ...CATEGORY_FILTER_ORDER.map((c) => ({
          value: String(c),
          label: t(categoryLabelKey(c)),
        })),
      ],
    },
    {
      id: 'status',
      label: t('manageBookings.status'),
      value: status,
      onChange: reset(setStatus),
      sort: false,
      options: [
        { value: ANY, label: t('manageBookings.any') },
        { value: String(BOOKING_STATUS.confirmed), label: t('manageBookings.confirmed') },
        { value: String(BOOKING_STATUS.pending), label: t('manageBookings.pending') },
        { value: String(BOOKING_STATUS.cancelled), label: t('manageBookings.cancelled') },
      ],
    },
  ];

  function runExport() {
    exportRows.mutate(model, {
      onSuccess: (data) => {
        if (!data?.length) {
          toast.info(t('manageBookings.nothingToExport'));
          return;
        }
        const keys = Object.keys(data[0]);
        const escape = (value: unknown) =>
          `"${String(value ?? '').replace(/"/g, '""')}"`;
        const csv = [
          keys.join(','),
          ...data.map((row) => keys.map((key) => escape(row[key])).join(',')),
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `bookings-${start}-to-${end}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      },
      onError: () => toast.error(t('manageBookings.exportFailed')),
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('manageBookings.title')}
        description={t('manageBookings.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={runExport}
            disabled={exportRows.isPending}
          >
            <Download />
            <span className="hidden sm:inline">
              {t('manageBookings.export')}
            </span>
          </Button>
        }
      />

      <BookingsTabs />

      <FilterBar
        busy={search.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void search.refetch();
              setQuery(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('manageBookings.searchPlaceholder')}
                aria-label={t('manageBookings.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={filterFields}
        summary={
          search.isSuccess ? (
            <p className="text-muted-foreground text-sm">
              {t('manageBookings.showingOf', { shown: rows.length, total })}
            </p>
          ) : null
        }
      />

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('manageBookings.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={() => void search.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {/* `isLoading`, not `isPending`: the search is off while a date is
          cleared, and a disabled query is pending for as long as it stays off. */}
      {search.isLoading && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-5 w-20" />
            </div>
          ))}
        </div>
      )}

      {search.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-14 text-center">
            <span className="bg-muted text-muted-foreground flex size-14 items-center justify-center rounded-full">
              <CalendarX2 className="size-7" />
            </span>
            <div className="space-y-1">
              <p className="font-medium">{t('manageBookings.noResultsTitle')}</p>
              <p className="text-muted-foreground max-w-sm text-sm">
                {t('manageBookings.noResultsBody')}
              </p>
            </div>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="hidden rounded-xl border lg:block">
            <Table busy={search.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('manageBookings.user')}</TableHead>
                  <TableHead>{t('manageBookings.event')}</TableHead>
                  <TableHead>{t('manageBookings.when')}</TableHead>
                  <TableHead>{t('manageBookings.status')}</TableHead>
                  <TableHead>{t('manageBookings.paidHow')}</TableHead>
                  <TableHead className="text-right">
                    {t('manageBookings.amount')}
                  </TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <p className="font-medium">
                        {[row.firstName, row.lastName].filter(Boolean).join(' ')}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {row.email}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {formatPhone(row.phoneNumber)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{row.eventName}</p>
                      <p className="text-muted-foreground text-xs">
                        {row.locationName}
                      </p>
                      <PackageTag
                        nameEn={row.packageNameEn}
                        nameEs={row.packageNameEs}
                        className="mt-1"
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDateTime(row.timeslotStartTime)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        {statusBadge(row.status, t)}
                        {row.checkedInAt && (
                          <Badge variant="success">
                            {t('manageBookings.checkedIn')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <PaymentCategoryCell row={row} language={language} />
                    </TableCell>
                    <TableCell className="text-right">
                      <BookingAmount row={row} />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1.5">
                        {canManageMoney &&
                          row.paymentCategory ===
                            PAYMENT_CATEGORY.awaitingPayment && (
                            <Button size="sm" onClick={() => setPaying(row)}>
                              <CreditCard className="size-3.5" />
                              {t('manageBookings.recordPayment')}
                            </Button>
                          )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDetailId(row.id)}
                        >
                          {t('manageBookings.view')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={search.isFetching} />
            <div
              className={cn(
                'space-y-2',
                search.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((row) => (
                <div
                  key={row.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium">
                        {[row.firstName, row.lastName].filter(Boolean).join(' ')}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {row.email}
                      </p>
                    </div>
                    <div className="text-right">
                      <BookingAmount row={row} />
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium">{row.eventName}</p>
                    <p className="text-muted-foreground text-xs">
                      {row.locationName} · {formatDateTime(row.timeslotStartTime)}
                    </p>
                    <PackageTag
                      nameEn={row.packageNameEn}
                      nameEs={row.packageNameEs}
                      className="mt-1.5"
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    {statusBadge(row.status, t)}
                    {row.checkedInAt && (
                      <Badge variant="success">
                        {t('manageBookings.checkedIn')}
                      </Badge>
                    )}
                  </div>

                  <PaymentCategoryCell row={row} language={language} />

                  <div className="flex gap-2">
                    {canManageMoney &&
                      row.paymentCategory === PAYMENT_CATEGORY.awaitingPayment && (
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={() => setPaying(row)}
                        >
                          <CreditCard className="size-3.5" />
                          {t('manageBookings.recordPayment')}
                        </Button>
                      )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setDetailId(row.id)}
                    >
                      {t('manageBookings.view')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}

      <BookingDetailSheet
        bookingId={detailId}
        open={detailId !== null}
        onOpenChange={(next) => {
          if (!next) setDetailId(null);
        }}
      />

      {/* A booking an admin assigned with payment bypassed has no outstanding
          balance because it has no purchase yet, so there is nothing to apply
          to and the dialog opens on a new request. */}
      <RecordPaymentDialog
        targetId={paying?.id ?? null}
        purchases={[]}
        open={paying !== null}
        onOpenChange={(next) => {
          if (!next) setPaying(null);
        }}
        onSaved={() => void search.refetch()}
      />
    </div>
  );
}
